import { VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../utils';

const textareaVariants = cva(
  ` flex w-full px-3 py-4 rounded-lg border border-border-default bg-background peer text-sm sm:text-sm
    focus:ring-2 focus:ring-offset-0 focus:ring-border-sky focus:border-surface focus-visible:outline-none
    disabled:bg-weak disabled:text-muted-foreground disabled:cursor-not-allowed
    hover:border-border-hover [&::-webkit-scrollbar-track]:my-2 [&::-webkit-scrollbar-corner]:rounded-full
    `,
  {
    variants: {
      hasError: {
        true: 'outline-critical',
      },
      floating: {
        true: 'pt-6 placeholder:text-transparent',
      },
      hasShadow: { true: 'shadow-sm disabled:shadow-none' },
    },
    defaultVariants: {
      hasError: false,
      floating: false,
      hasShadow: false,
    },
  },
);

export interface TextareaProps
  extends React.ComponentProps<'textarea'>,
    VariantProps<typeof textareaVariants> {
  required?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      hasError,
      name,
      placeholder,
      hasShadow,
      floating = false,
      required = false,
      ...props
    },
    ref,
  ) => {
    return (
      <div className="w-full relative">
        <textarea
          className={cn(textareaVariants({ hasError, floating, hasShadow }), className)}
          placeholder={placeholder}
          ref={ref}
          id={name}
          {...props}
        />

        {placeholder && floating && (
          <label
            className={cn(
              `z-10 absolute text-sm text-weak duration-300 left-3 -translate-y-3 scale-75 w-[125%] top-4.5 py-2 -mt-2.5 bg-background origin-[0]
              peer-placeholder-shown:scale-100 peer-placeholder-shown:w-auto peer-placeholder-shown:p-0 peer-placeholder-shown:translate-y-0
              peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:py-2
              peer-disabled:z-0 cursor-text peer-autofill:scale-75`,
              { "after:content-['*'] after:text-critical": required },
            )}
            htmlFor={name}
          >
            {placeholder}
          </label>
        )}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';

export { Textarea };
